import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router';
import Waypoint from 'react-waypoint';
import * as yup from 'yup';
import { useDebounceCallback } from '../../../../hooks/useDebounceValue';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getDisplayName } from '../../../../models/NameUtils';
import { useUserQuery } from '../../../../queries';
import { useAdminAccountExportMutation } from '../../../../queries/Admin/Account/useAdminAccountExportMutation';
import { useAdminAccountFilterQuery } from '../../../../queries/Admin/Account/useAdminAccountFilterQuery';
import * as AdminApi from '../../../../viewmodels/AdminViewModels';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownRadioGroup,
	DropdownRadioItem,
	DropdownSelectTrigger,
} from '../../../../web/components-v2/Dropdown';
import { Input, InputWrapper } from '../../../../web/components-v2/Input';
import { Breadcrumb, BreadcrumbList, BreadcrumbPage } from '../../../../web/components/Breadcrumb';
import { ExportConfirmationModalV2 } from '../../../../web/components/ExportConfirmation';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Portal } from '../../../../web/components/Portal';
import {
	IRadioButtonDialogOption,
	RadioButtonOptionsDialog,
} from '../../../../web/components/RadioButtonOptionsDialog';
import {
	Select,
	SelectCheckboxItem,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '../../../../web/components/Select';
import { ExportIcon } from '../../../../web/components/svgs/icons/ExportIcon';
import { SearchIcon2 } from '../../../../web/components/svgs/icons/SearchIcon2';
import { UserSelectModal } from '../../../../web/components/UserSelectModal';
import { brandPrimaryText } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { AccountRow } from '../../../components/AccountRow';
import { AccountsStatsHeader } from '../../../components/AccountsStatsHeader';
import { appendBillingCycleCriteria, BILLING_CYCLE_OPTIONS, BillingCycle } from '../../reports/ReportsMonthlyRenewal';
import { styleSheet } from './styles';

const SESSION_STORAGE_KEY_LATEST_ADMIN_ACCOUNTS_URL = `admin-latest-accounts-url`;
export const getSessionAdminAccountsUrl = () => {
	return (sessionStorage.getItem(SESSION_STORAGE_KEY_LATEST_ADMIN_ACCOUNTS_URL) ?? Api.isProdEnvironment)
		? `/accounts?planId=business-paid`
		: `/accounts`;
};

enum PlanFilterOptions {
	All = 'all',
	Business = 'business',
	BusinessPaid = 'business-paid',
	Personal = 'personal',
}

const planOptions = [
	{ label: 'All Accounts', value: PlanFilterOptions.All },
	{ label: 'Business', value: PlanFilterOptions.Business },
	{ label: 'Business Paid', value: PlanFilterOptions.BusinessPaid },
	{ label: 'Personal', value: PlanFilterOptions.Personal },
];

const industryOptions: Api.Industry[] = [
	Api.Industry.Insurance,
	Api.Industry.RealEstate,
	Api.Industry.Financial,
	Api.Industry.Mortgage,
	Api.Industry.Legal,
	Api.Industry.Accounting,
	Api.Industry.NonProfit,
	Api.Industry.HomeMaintenance,
];

const industryRadios: IRadioButtonDialogOption<Api.Industry>[] = industryOptions.map(x => {
	return {
		key: x,
		representedObject: x,
		title: x,
	};
});

const productSearchParamsSchema = yup.object().shape({
	green: yup.boolean(),
	grey: yup.boolean(),
	orange: yup.boolean(),
	red: yup.boolean(),
	yellow: yup.boolean(),
	flagged: yup.boolean(),
	searchQuery: yup.string(),
	planId: yup.string().oneOf(Object.values(PlanFilterOptions)),
	billing: yup.array().of(yup.mixed<BillingCycle>().oneOf(Object.values(BillingCycle) as BillingCycle[])),
	sortBy: yup.string(),
	sort: yup.string<'asc' | 'desc'>().oneOf(['asc', 'desc']),
	accountFilter: yup.string(),
	hasNoSalesNotes: yup.boolean(),
	hasAds: yup.boolean(),
	customCalendarEnabled: yup.boolean(),
	customCalendarIntakeFormNeeded: yup.boolean(),
	hasWebsites: yup.boolean(),
});

enum AccountFilterSearchParamType {
	all = 'all',
	ownedBy = 'ownedBy',
	soldBy = 'soldBy',
	industry = 'industry',
}
type AccountFilterSearchParam =
	| { type: AccountFilterSearchParamType.all }
	| { type: AccountFilterSearchParamType.ownedBy; userId: string }
	| { type: AccountFilterSearchParamType.soldBy; userId: string }
	| { type: AccountFilterSearchParamType.industry; industry: Api.Industry };

export function useAccountsSearchParams() {
	const history = useHistory();
	const location = useLocation();
	const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

	const {
		green,
		grey,
		orange,
		red,
		yellow,
		flagged,
		searchQuery,
		planId,
		billing,
		sort,
		sortBy,
		accountFilter: accountFilterParam,
		hasNoSalesNotes,
		hasAds,
		customCalendarEnabled,
		customCalendarIntakeFormNeeded,
		hasWebsites,
	} = productSearchParamsSchema.cast(
		{
			green: searchParams.get('green')?.toLowerCase() ?? false,
			grey: searchParams.get('grey')?.toLowerCase() ?? false,
			orange: searchParams.get('orange')?.toLowerCase() ?? false,
			red: searchParams.get('red')?.toLowerCase() ?? false,
			yellow: searchParams.get('yellow')?.toLowerCase() ?? false,
			flagged: searchParams.get('flagged')?.toLowerCase() ?? false,
			searchQuery: searchParams.get('searchQuery') ?? '',
			planId: searchParams.get('planId') ?? PlanFilterOptions.All,
			billing: searchParams.get('billing')
				? searchParams
						.get('billing')
						.split(',')
						.map(x => parseInt(x))
				: [BillingCycle.All],
			sortBy: searchParams.get('sortBy') ?? 'creationDate',
			sort: searchParams.get('sort') ?? 'desc',
			accountFilter: searchParams.get('accountFilter') ?? '',
			hasNoSalesNotes: searchParams.get('hasNoSalesNotes')?.toLowerCase() ?? false,
			hasAds: searchParams.get('hasAds')?.toLowerCase() ?? false,
			customCalendarEnabled: searchParams.get('customCalendarEnabled')?.toLowerCase() ?? false,
			customCalendarIntakeFormNeeded: searchParams.get('customCalendarIntakeFormNeeded')?.toLowerCase() ?? false,
			hasWebsites: searchParams.get('hasWebsites')?.toLowerCase() ?? false,
		},
		{ assert: false }
	);
	let accountFilter: AccountFilterSearchParam = { type: AccountFilterSearchParamType.all };
	if (accountFilterParam.startsWith(AccountFilterSearchParamType.ownedBy)) {
		const ownedById = accountFilterParam.split(':')[1];
		if (ownedById) {
			accountFilter = { type: AccountFilterSearchParamType.ownedBy, userId: ownedById };
		}
	}
	if (accountFilterParam.startsWith(AccountFilterSearchParamType.soldBy)) {
		const soldById = accountFilterParam.split(':')[1];
		if (soldById) {
			accountFilter = { type: AccountFilterSearchParamType.soldBy, userId: soldById };
		}
	}
	if (accountFilterParam.startsWith(AccountFilterSearchParamType.industry)) {
		const industry = accountFilterParam.split(':')[1] as Api.Industry;
		if (industry) {
			accountFilter = { type: AccountFilterSearchParamType.industry, industry };
		}
	}

	const setParam = React.useCallback(
		function (paramName: string, paramValue: string) {
			const newSearchParams = new URLSearchParams(searchParams);
			if (paramValue) {
				newSearchParams.set(paramName, paramValue);
			} else {
				newSearchParams.delete(paramName);
			}
			history.replace({ pathname: location.pathname, search: newSearchParams.toString() });
		},
		[history, location.pathname, searchParams]
	);

	const setAccountFilter = (value: AccountFilterSearchParam) => {
		switch (value.type) {
			case 'ownedBy':
				setParam('accountFilter', `ownedBy:${value.userId}`);
				break;
			case 'soldBy':
				setParam('accountFilter', `soldBy:${value.userId}`);
				break;
			case 'industry':
				setParam('accountFilter', `industry:${value.industry}`);
				break;
			default:
				setParam('accountFilter', '');
		}
	};

	return {
		green,
		grey,
		orange,
		red,
		yellow,
		searchQuery,
		flagged,
		planId,
		billing,
		sort,
		sortBy,
		accountFilter,
		hasNoSalesNotes,
		hasAds,
		customCalendarEnabled,
		customCalendarIntakeFormNeeded,
		hasWebsites,
		setGreen: (value: boolean) => setParam('green', value ? 'true' : null),
		setGrey: (value: boolean) => setParam('grey', value ? 'true' : null),
		setOrange: (value: boolean) => setParam('orange', value ? 'true' : null),
		setRed: (value: boolean) => setParam('red', value ? 'true' : null),
		setYellow: (value: boolean) => setParam('yellow', value ? 'true' : null),
		setSearchQuery: React.useCallback((value: string) => setParam('searchQuery', value), [setParam]),
		setFlagged: (value: boolean) => setParam('flagged', value ? 'true' : null),
		setPlanId: (value: PlanFilterOptions) => setParam('planId', value),
		setBilling: (value: BillingCycle[]) => setParam('billing', value.join(',')),
		setSort: (value: 'asc' | 'desc') => setParam('sort', value),
		setSortBy: (value: string) => setParam('sortBy', value),
		setAccountFilter,
		setHasNoSalesNotes: (value: boolean) => setParam('hasNoSalesNotes', value ? 'true' : null),
		setHasAds: (value: boolean) => setParam('hasAds', value ? 'true' : null),
		setCustomCalendarEnabled: (value: boolean) => setParam('customCalendarEnabled', value ? 'true' : null),
		setCustomCalendarIntakeFormNeeded: (value: boolean) =>
			setParam('customCalendarIntakeFormNeeded', value ? 'true' : null),
		setHasWebsites: (value: boolean) => setParam('hasWebsites', value ? 'true' : null),
	};
}

export function Accounts() {
	const errorMessages = useErrorMessages();
	const history = useHistory();
	const userSession = useUserSession();
	const location = useLocation();
	const { logApiError } = useEventLogging('Accounts');
	const {
		flagged,
		green,
		grey,
		orange,
		red,
		yellow,
		planId,
		sort,
		sortBy,
		accountFilter,
		hasNoSalesNotes,
		hasAds,
		customCalendarEnabled,
		customCalendarIntakeFormNeeded,
		hasWebsites,
		setGreen,
		setOrange,
		setRed,
		setYellow,
		setGrey,
		searchQuery,
		setSearchQuery,
		setFlagged,
		setPlanId,
		billing,
		setBilling,
		setSort,
		setSortBy,
		setAccountFilter,
		setHasNoSalesNotes,
		setHasAds,
		setCustomCalendarEnabled,
		setCustomCalendarIntakeFormNeeded,
		setHasWebsites,
	} = useAccountsSearchParams();
	const [searchValue, setSearchValue] = React.useState(searchQuery);
	const debouncedSetSearchQuery = useDebounceCallback(setSearchQuery);
	const handleSearchInputChange = (value: string) => {
		setSearchValue(value);
		debouncedSetSearchQuery(value);
	};
	const handleClearFilters = () => {
		history.replace(`/accounts`);
	};
	React.useEffect(() => {
		if (searchQuery !== searchValue) {
			setSearchValue(searchQuery);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery]);
	const [showingOwnedByModal, setShowingOwnedByModal] = React.useState(false);
	const [showingSoldByModal, setShowingSoldByModal] = React.useState(false);
	const [showingIndustryModal, setShowingIndustryModal] = React.useState(false);
	const adminAccountExportMutation = useAdminAccountExportMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('Accounts', error);
		},
	});
	const advancedFiltersState = React.useMemo(
		() => ({
			hasNoSalesNotes,
			hasAds,
			customCalendarEnabled,
			customCalendarIntakeFormNeeded,
			hasWebsites,
		}),
		[hasNoSalesNotes, hasAds, customCalendarEnabled, customCalendarIntakeFormNeeded, hasWebsites]
	);

	const accountFilterRequest = React.useMemo(() => {
		const criteria: AdminApi.IAccountFilterCriteria[] = []; // and'ed
		const colorFilterCriteria: AdminApi.IAccountFilterCriteria[] = []; // or'ed
		if (searchQuery.length > 0) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Name,
				value: searchQuery,
			});
		}
		if (planId === 'business' || planId === 'personal') {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.AccountType,
				value: planId,
			});
		}
		if (planId === PlanFilterOptions.BusinessPaid) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.AccountType,
				value: 'business',
			});
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.BillingType,
				value: Api.BillingType.Paid,
			});
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.CancellationDate,
				value: 'false',
			});
		}
		if (accountFilter.type === AccountFilterSearchParamType.ownedBy) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.SuccessManager,
				value: accountFilter.userId,
			});
		}
		if (accountFilter.type === AccountFilterSearchParamType.soldBy) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.SalesRep,
				value: accountFilter.userId,
			});
		}
		if (accountFilter.type === AccountFilterSearchParamType.industry) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Industry,
				value: accountFilter.industry,
			});
		}
		if (green) {
			colorFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.ActiveUseStatus,
				value: 'green',
			});
		}
		if (grey) {
			colorFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.ActiveUseStatus,
				value: 'gray',
			});
		}
		if (yellow) {
			colorFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.ActiveUseStatus,
				value: 'yellow',
			});
		}
		if (orange) {
			colorFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.ActiveUseStatus,
				value: 'orange',
			});
		}
		if (red) {
			colorFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.ActiveUseStatus,
				value: 'red',
			});
		}
		if (flagged) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Flagged,
				value: 'true',
			});
		}
		if (colorFilterCriteria?.length > 0) {
			criteria.push({
				criteria: colorFilterCriteria,
				op: Api.FilterOperator.Or,
			});
		}
		appendBillingCycleCriteria({ billingCycleFilter: billing, criteria });

		// Advanced filters (OR logic)
		const advancedFilterCriteria: AdminApi.IAccountFilterCriteria[] = [];
		if (advancedFiltersState.hasNoSalesNotes) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.HasNoSalesNotes,
				value: 'true',
			});
		}
		if (advancedFiltersState.hasAds) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.HasAds,
				value: 'true',
			});
		}
		if (advancedFiltersState.customCalendarEnabled) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.CustomCalendarEnabled,
				value: 'true',
			});
		}
		if (advancedFiltersState.customCalendarIntakeFormNeeded) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.CustomCalendarIntakeFormNeeded,
				value: 'true',
			});
		}
		if (advancedFiltersState.hasWebsites) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Websites,
				value: 'true',
			});
		}
		if (advancedFilterCriteria?.length > 0) {
			criteria.push({
				criteria: advancedFilterCriteria,
				op: Api.FilterOperator.Or,
			});
		}

		const finalRequest = {
			criteria: [...criteria],
			op: Api.FilterOperator.And,
		} as AdminApi.IAccountsFilterRequest;

		return finalRequest;
	}, [planId, searchQuery, billing, accountFilter, advancedFiltersState, flagged, green, orange, red, yellow, grey]);
	const adminAccountFilterQuery = useAdminAccountFilterQuery({
		filter: accountFilterRequest,
		sort,
		sortBy,
		autocompleteFragment: searchQuery ? searchQuery : undefined,
	});
	const soldByUserQuery = useUserQuery({
		id: accountFilter.type === AccountFilterSearchParamType.soldBy ? accountFilter.userId : '',
		enabled: accountFilter.type === AccountFilterSearchParamType.soldBy && Boolean(accountFilter.userId),
	});
	const ownedByUserQuery = useUserQuery({
		id: accountFilter.type === AccountFilterSearchParamType.ownedBy ? accountFilter.userId : '',
		enabled: accountFilter.type === AccountFilterSearchParamType.ownedBy && Boolean(accountFilter.userId),
	});
	React.useEffect(() => {
		document.title = 'Admin';
	}, [adminAccountFilterQuery.data]);
	React.useEffect(() => {
		if (adminAccountFilterQuery.error) {
			errorMessages?.pushApiError('Could not load the account information');
		}
	}, [adminAccountFilterQuery.error, errorMessages]);
	React.useEffect(() => {
		sessionStorage.setItem(SESSION_STORAGE_KEY_LATEST_ADMIN_ACCOUNTS_URL, location.pathname + location.search);
	}, [location.pathname, location.search]);

	let accountDropdownSelectedText = 'All Accounts';
	if (accountFilter.type === AccountFilterSearchParamType.ownedBy) {
		accountDropdownSelectedText =
			accountFilter.userId === userSession.user.id
				? 'Accounts I Own'
				: `Accounts Owned By ${ownedByUserQuery.data ? getDisplayName(ownedByUserQuery.data) : ''}`;
	} else if (accountFilter.type === AccountFilterSearchParamType.soldBy) {
		accountDropdownSelectedText =
			accountFilter.userId === userSession.user.id
				? 'Accounts I Sold'
				: `Accounts Sold By ${soldByUserQuery.data ? getDisplayName(soldByUserQuery.data) : ''}`;
	} else if (accountFilter.type === AccountFilterSearchParamType.industry) {
		accountDropdownSelectedText = `Accounts by industry: ${accountFilter.industry}`;
	}
	const isOwnedByMode = accountFilter.type === AccountFilterSearchParamType.ownedBy;
	const planFilterChange = (option: PlanFilterOptions) => {
		if (option !== planId) {
			setPlanId(option);
		}
	};
	const newAccountClick = () => {
		history.push('/accounts/create-account');
	};

	const getAdvancedFilterDisplayText = () => {
		if (!hasNoSalesNotes && !hasAds && !customCalendarEnabled && !customCalendarIntakeFormNeeded && !hasWebsites) {
			return 'None selected';
		}

		const selectedFilters = [
			hasNoSalesNotes ? 'No Sales Notes' : '',
			customCalendarEnabled ? 'Custom Content' : '',
			customCalendarIntakeFormNeeded ? 'Content Intake Needed' : '',
			hasAds ? 'Ads' : '',
			hasWebsites ? 'Websites' : '',
		].filter(Boolean);

		return selectedFilters.join(', ');
	};
	const onExportAccountsButtonClicked = () => {
		adminAccountExportMutation.mutate({
			request: {
				filter: accountFilterRequest,
				useSnapshots: false,
			},
		});
	};
	const sortByDate = (sortDescriptor: Api.ISortDescriptor) => () => {
		if (sort !== sortDescriptor.sort) {
			setSort(sortDescriptor.sort);
		}
		if (sortBy !== sortDescriptor.sortBy) {
			setSortBy(sortDescriptor.sortBy);
		}
	};
	const onOwnedByModalRequestClose = (user?: Api.UserViewModel, cancel?: boolean) => {
		if (!cancel) {
			setAccountFilter({ type: AccountFilterSearchParamType.ownedBy, userId: user.id });
		}
		setShowingOwnedByModal(false);
	};
	const onSoldByModalRequestClose = (user?: Api.UserViewModel, cancel?: boolean) => {
		if (!cancel) {
			setAccountFilter({ type: AccountFilterSearchParamType.soldBy, userId: user.id });
		}
		setShowingSoldByModal(false);
	};
	const onRemoveFromTagDialogRequestClose = (result?: IRadioButtonDialogOption<Api.Industry>, canceled?: boolean) => {
		if (!canceled) {
			setAccountFilter({ type: AccountFilterSearchParamType.industry, industry: result.representedObject });
		}
		setShowingIndustryModal(false);
	};

	let accountsTitle = 'All Accounts';

	if (accountFilter.type === AccountFilterSearchParamType.ownedBy && accountFilter.userId === userSession.user.id) {
		accountsTitle = userSession.user.firstName + ' ' + userSession.user.lastName;
	} else if (accountFilter.type === AccountFilterSearchParamType.ownedBy && ownedByUserQuery.data) {
		accountsTitle = getDisplayName(ownedByUserQuery.data);
	} else if (accountFilter.type === AccountFilterSearchParamType.soldBy && soldByUserQuery.data) {
		accountsTitle = getDisplayName(soldByUserQuery.data);
	} else if (accountFilter.type === AccountFilterSearchParamType.industry) {
		accountsTitle = `${accountFilter.industry} Accounts`;
	} else if (planId === PlanFilterOptions.BusinessPaid) {
		accountsTitle = 'All Paid Accounts';
	}
	const isFiltered =
		flagged ||
		green ||
		grey ||
		yellow ||
		orange ||
		red ||
		isOwnedByMode ||
		planId !== PlanFilterOptions.BusinessPaid ||
		!!searchQuery ||
		!!sort ||
		!!sortBy ||
		accountFilter.type === AccountFilterSearchParamType.all ||
		hasNoSalesNotes ||
		hasAds ||
		customCalendarEnabled ||
		customCalendarIntakeFormNeeded ||
		hasWebsites;

	const renderCreatedHeader = () => {
		if (sort === 'asc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({ sort: 'desc', sortBy: 'creationDate' })}
					style={{
						color: sortBy === 'creationDate' ? 'black' : '',
					}}
				>
					Created &#x25B2;
				</div>
			);
		} else if (sort === 'desc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({ sort: 'asc', sortBy: 'creationDate' })}
					style={{
						color: sortBy === 'creationDate' ? 'black' : '',
					}}
				>
					Created &#x25BC;
				</div>
			);
		}
	};
	const renderRenewalHeader = () => {
		if (sort === 'asc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({
						sort: 'desc',
						sortBy: 'planDetails.renewalDate',
					})}
					style={{
						color: sortBy === 'planDetails.renewalDate' ? 'black' : '',
					}}
				>
					Renewal &#x25B2;
				</div>
			);
		} else if (sort === 'desc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({
						sort: 'asc',
						sortBy: 'planDetails.renewalDate',
					})}
					style={{
						color: sortBy === 'planDetails.renewalDate' ? 'black' : '',
					}}
				>
					Renewal &#x25BC;
				</div>
			);
		}
	};
	const renderLastMeeting = () => {
		if (sort === 'asc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({
						sort: 'desc',
						sortBy: 'additionalInfo.lastCsmMeeting',
					})}
					style={{
						color: sortBy === 'additionalInfo.lastCsmMeeting' ? 'black' : '',
					}}
				>
					Last Meeting &#x25B2;
				</div>
			);
		} else if (sort === 'desc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({
						sort: 'asc',
						sortBy: 'additionalInfo.lastCsmMeeting',
					})}
					style={{
						color: sortBy === 'additionalInfo.lastCsmMeeting' ? 'black' : '',
					}}
				>
					Last Meeting &#x25BC;
				</div>
			);
		}
	};
	const renderNextMeeting = () => {
		if (sort === 'asc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderLast)}
					onClick={sortByDate({
						sort: 'desc',
						sortBy: 'additionalInfo.nextMeeting',
					})}
					style={{
						color: sortBy === 'additionalInfo.nextMeeting' ? 'black' : '',
					}}
				>
					Next Meeting &#x25B2;
				</div>
			);
		} else if (sort === 'desc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderLast)}
					onClick={sortByDate({
						sort: 'asc',
						sortBy: 'additionalInfo.nextMeeting',
					})}
					style={{
						color: sortBy === 'additionalInfo.nextMeeting' ? 'black' : '',
					}}
				>
					Next Meeting &#x25BC;
				</div>
			);
		}
	};
	return (
		<div className={css(styleSheet.root)} data-scroll-restoration-id='admin-accounts-page'>
			<Portal destination={MastheadPortalId}>
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbPage>Accounts</BreadcrumbPage>
					</BreadcrumbList>
				</Breadcrumb>
			</Portal>
			<div className={css(styleSheet.contentContainer)}>
				<div className={css(styleSheet.actionBarContainer)}>
					<div className={css(styleSheet.actionBarFilters)}>
						<InputWrapper styles={[styleSheet.triggerContainer]}>
							<SearchIcon2 width={16} height={16} />
							<Input
								onChange={ev => handleSearchInputChange(ev.target.value)}
								placeholder='Search Company'
								value={searchValue || ''}
							/>
						</InputWrapper>
						<span className={css(styleSheet.dropdownContainerWithMargin)}>
							<Select items={planOptions} value={planId} onValueChange={planFilterChange}>
								<SelectTrigger styles={[styleSheet.typeSelectTrigger]}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={PlanFilterOptions.All}>All Accounts</SelectItem>
									<SelectItem value={PlanFilterOptions.Business}>Business</SelectItem>
									<SelectItem value={PlanFilterOptions.BusinessPaid}>Business Paid</SelectItem>
									<SelectItem value={PlanFilterOptions.Personal}>Personal</SelectItem>
								</SelectContent>
							</Select>
						</span>
						<span className={css(styleSheet.dropdownContainerWithMargin)}>
							<Dropdown>
								<DropdownSelectTrigger styles={[styleSheet.selectTrigger]}>
									{accountDropdownSelectedText}
								</DropdownSelectTrigger>
								<DropdownContent>
									<DropdownRadioGroup
										value={
											accountFilter.type === AccountFilterSearchParamType.ownedBy &&
											accountFilter.userId === userSession.user.id
												? 'ownedByMe'
												: accountFilter.type === AccountFilterSearchParamType.soldBy &&
														accountFilter.userId === userSession.user.id
													? 'soldByMe'
													: accountFilter.type
										}
									>
										<DropdownRadioItem
											value={AccountFilterSearchParamType.all}
											onClick={() => setAccountFilter({ type: AccountFilterSearchParamType.all })}
										>
											All accounts
										</DropdownRadioItem>
										<DropdownRadioItem
											value={'ownedByMe'}
											onClick={() =>
												setAccountFilter({ type: AccountFilterSearchParamType.ownedBy, userId: userSession.user.id })
											}
										>
											Accounts I own
										</DropdownRadioItem>
										<DropdownRadioItem
											value={AccountFilterSearchParamType.ownedBy}
											onClick={() => setShowingOwnedByModal(true)}
											closeOnClick
										>
											Accounts owned by...
										</DropdownRadioItem>
										<DropdownRadioItem
											value={'soldByMe'}
											onClick={() =>
												setAccountFilter({ type: AccountFilterSearchParamType.soldBy, userId: userSession.user.id })
											}
										>
											Accounts I sold
										</DropdownRadioItem>
										<DropdownRadioItem
											value={AccountFilterSearchParamType.soldBy}
											onClick={() => setShowingSoldByModal(true)}
											closeOnClick
										>
											Accounts sold by...
										</DropdownRadioItem>
										<DropdownRadioItem
											value={AccountFilterSearchParamType.industry}
											onClick={() => setShowingIndustryModal(true)}
											closeOnClick
										>
											Accounts by industry...
										</DropdownRadioItem>
									</DropdownRadioGroup>
								</DropdownContent>
							</Dropdown>
						</span>
						<span className={css(styleSheet.dropdownContainerWithMargin)}>
							<Select
								multiple
								items={BILLING_CYCLE_OPTIONS.map(o => ({
									label: o.text,
									value: o.dataContext,
								}))}
								value={billing}
								onValueChange={setBilling}
							>
								<SelectTrigger>
									<SelectValue>
										{billing.includes(BillingCycle.All)
											? 'All Billing Cycles'
											: `Renewal period: ${billing.sort((a, b) => a - b).join(', ')} months`}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									{BILLING_CYCLE_OPTIONS.map(o => {
										return (
											<SelectCheckboxItem key={o.dataContext} value={o.dataContext}>
												{o.text}
											</SelectCheckboxItem>
										);
									})}
								</SelectContent>
							</Select>
						</span>
						<span className={css(styleSheet.dropdownContainer)}>
							<span className={css(styleSheet.searchBarText)}>Advanced: </span>
							<Dropdown>
								<DropdownSelectTrigger styles={[styleSheet.selectTrigger]}>
									{getAdvancedFilterDisplayText()}
								</DropdownSelectTrigger>
								<DropdownContent>
									<DropdownCheckboxItem checked={hasNoSalesNotes} onCheckedChange={setHasNoSalesNotes}>
										No Sales Notes
									</DropdownCheckboxItem>
									<DropdownCheckboxItem checked={customCalendarEnabled} onCheckedChange={setCustomCalendarEnabled}>
										Custom Content
									</DropdownCheckboxItem>
									<DropdownCheckboxItem
										checked={customCalendarIntakeFormNeeded}
										onCheckedChange={setCustomCalendarIntakeFormNeeded}
									>
										Content Intake Needed
									</DropdownCheckboxItem>
									<DropdownCheckboxItem checked={hasAds} onCheckedChange={setHasAds}>
										Ads
									</DropdownCheckboxItem>
									<DropdownCheckboxItem checked={hasWebsites} onCheckedChange={setHasWebsites}>
										Websites
									</DropdownCheckboxItem>
								</DropdownContent>
							</Dropdown>
						</span>
					</div>
					<div className={css(baseStyleSheet.horizontalStack)}>
						<button className={css(baseStyleSheet.ctaButton, styleSheet.ctaButton)} onClick={newAccountClick}>
							<span>New Account</span>
						</button>
					</div>
				</div>
				<div className={css(styleSheet.accountsTitleHeader)}>
					{!isFiltered ? (
						<div className={css(styleSheet.accountsTitle)}>{accountsTitle}</div>
					) : (
						<div className={css(styleSheet.accountsTitle)}>
							{accountsTitle}
							<button className={css(styleSheet.clearFilters)} onClick={handleClearFilters}>
								Clear filters
							</button>
						</div>
					)}
					<button className={css(styleSheet.exportButton)} onClick={onExportAccountsButtonClicked}>
						<ExportIcon fillColor={brandPrimaryText} />
						<span style={{ marginLeft: 6 }}>Export to CSV</span>
					</button>
				</div>
				<ExportConfirmationModalV2
					mutationResult={adminAccountExportMutation}
					modalProps={{
						isOpen: !adminAccountExportMutation.isIdle,
						onRequestClose: () => adminAccountExportMutation.reset(),
					}}
				/>
				<AccountsStatsHeader
					filterCriteria={accountFilterRequest}
					flaggedCheck={flagged}
					greenCheck={green}
					greyCheck={grey}
					onChangedFlaggedCheck={() => setFlagged(!flagged)}
					onChangedGreenCheck={() => setGreen(!green)}
					onChangedGreyCheck={() => setGrey(!grey)}
					onChangedOrangeCheck={() => setOrange(!orange)}
					onChangedRedCheck={() => setRed(!red)}
					onChangedYellowCheck={() => setYellow(!yellow)}
					orangeCheck={orange}
					redCheck={red}
					yellowCheck={yellow}
					totalNumberOfResults={adminAccountFilterQuery.data?.pages?.[0]?.totalCount}
				/>
				<div className={css(styleSheet.accountListHeader)}>
					<div className={css(styleSheet.accountListHeaderCompanyName)}>Company Name</div>
					{isOwnedByMode ? (
						<>
							<div className={css(styleSheet.accountListHeaderStandard)}>Industry</div>
							<div className={css(styleSheet.accountListHeaderStandard)}>Stage</div>
							{renderCreatedHeader()}
							{renderRenewalHeader()}
							{renderLastMeeting()}
							{renderNextMeeting()}
						</>
					) : (
						<>
							<div className={css(styleSheet.accountListHeaderStandard)}>Industry</div>
							<div className={css(styleSheet.accountListHeaderStandard)}>Success</div>
							{renderCreatedHeader()}
							{renderRenewalHeader()}
							{renderLastMeeting()}
							{renderNextMeeting()}
						</>
					)}
				</div>
				<div>
					{adminAccountFilterQuery.data?.pages.map(page => {
						return page.values.map(account => {
							return (
								<AccountRow
									accountVM={new AdminApi.AccountAdminViewModel(userSession, account)}
									isOwnedByMode={isOwnedByMode}
									key={account.id}
								/>
							);
						});
					})}
					{adminAccountFilterQuery.hasNextPage && !adminAccountFilterQuery.isPending ? (
						<Waypoint bottomOffset='-200px' onEnter={() => adminAccountFilterQuery.fetchNextPage()} />
					) : null}
					{adminAccountFilterQuery.isFetchingNextPage ? <LoadingSpinner type='large' /> : null}
				</div>
			</div>
			<UserSelectModal
				modalProps={{
					isOpen: showingOwnedByModal,
					onRequestClose: onOwnedByModalRequestClose,
				}}
				title='Accounts owned by'
			/>
			<UserSelectModal
				modalProps={{
					isOpen: showingSoldByModal,
					onRequestClose: onSoldByModalRequestClose,
				}}
				title='Accounts sold by'
			/>
			<RadioButtonOptionsDialog
				modalProps={{
					isOpen: !!showingIndustryModal,
					onRequestClose: onRemoveFromTagDialogRequestClose,
				}}
				options={industryRadios}
				title='Accounts by industry'
			/>
		</div>
	);
}
